import {
  BlotterTable,
  type CareOrder,
  CareOrderStatus,
  type CareOrderStatusEnum,
  type ColumnDef,
  formattedUTCDay,
  isGridApiReady,
  logger,
  type MinimalSubscriptionResponse,
  type SizeColumnParams,
  Tooltip,
  useBehaviorSubject,
  useBlotterTable,
  type ValueOf,
  WARNING_CELL_MED_CLASSNAME,
  WarningSeverity,
  WarningSeverityIcon,
} from '@talos/kyoko';
import type { CellClassRules, CellStyle, GridOptions, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo, useRef } from 'react';
import type { CareOrderParser, ImportedCareOrder, SimilarCareOrder } from '../types';

interface SimilarCareOrdersBlotterProps {
  importedCareOrder: ImportedCareOrder;
  initialSelectedCareOrder?: CareOrder;
  onSelectionChange: (selectedCareOrder: CareOrder | undefined) => void;
  parser: CareOrderParser;
}

export const SimilarCareOrdersBlotter = ({
  importedCareOrder,
  initialSelectedCareOrder,
  onSelectionChange,
  parser,
}: SimilarCareOrdersBlotterProps) => {
  const { observable: similarCareOrdersDataObs } = useBehaviorSubject(
    () =>
      ({
        initial: true,
        data: importedCareOrder?.action.similarCareOrders ?? [],
      }) satisfies MinimalSubscriptionResponse<SimilarCareOrder>,
    [importedCareOrder]
  );

  const didSelectInitialRow = useRef(false);
  const similarCareOrdersBlotterGridOptions: GridOptions<SimilarCareOrder> = useMemo(
    () => ({
      defaultColDef: {
        suppressHeaderMenuButton: true, // dont allow modifying columns in this dialog
      },
      rowSelection: {
        checkboxes: true,
        mode: 'singleRow',
        enableClickSelection: true,
        hideDisabledCheckboxes: true,
        isRowSelectable: params => {
          const sco = params.data;
          if (!sco) {
            return false;
          }

          if (!sco.careOrder.isModifiable) {
            return false;
          }

          if (sco.warning != null) {
            return false;
          }

          return true;
        },
      },

      onRowDataUpdated: event => {
        if (!isGridApiReady(event.api)) {
          return;
        }
        if (didSelectInitialRow.current) {
          return;
        }
        if (initialSelectedCareOrder) {
          // Try to select our initial row once we get the first data update
          event.api.forEachNode(node => {
            if (node.data?.careOrder.ClOrdID === initialSelectedCareOrder.ClOrdID) {
              node.setSelected(true);
            }
          });
        }
      },
      onSelectionChanged: event => onSelectionChange(event.api.getSelectedRows().at(0)?.careOrder),
    }),
    [onSelectionChange, initialSelectedCareOrder]
  );

  const similarCareOrdersColumns = useSimilarCareOrdersColumns(parser);
  const similarCareOrdersBlotter = useBlotterTable({
    dataObservable: similarCareOrdersDataObs,
    errorObservable: undefined,
    rowID: 'rowID' satisfies keyof SimilarCareOrder,
    columns: similarCareOrdersColumns,
    gridOptions: similarCareOrdersBlotterGridOptions,
  });

  return <BlotterTable {...similarCareOrdersBlotter} background="backgroundModal" />;
};

/**
 * We want to align all three blotters in the ImportActionDialog. To do so, we define the widths here for the SimilarCareOrdersBlotter, and then
 * the other blotters use these as well. All 3 blotters have the same column setup.
 */
export const SIMILAR_CARE_ORDERS_COL_WIDTHS = {
  warning: 28,
  symbol: 80,
  fund: 80,
  side: 60,
  orderQty: 150,
  units: 100,
  status: 100,
  submitTime: 140,
  group: 140,
  initiatingFrom: 140,
  comments: 140,
  counterparty: 140,
  settleDate: 100,
};

export const useSimilarCareOrdersColumns = (parser: CareOrderParser) => {
  const cellClassRules: CellClassRules<SimilarCareOrder, unknown> = useMemo(() => {
    return {
      [WARNING_CELL_MED_CLASSNAME]: params => {
        const field = params.colDef.field;
        if (!field) {
          logger.warn(`Unable to compute cell warning class for column without a field. colID: ${params.colDef.colId}`);
          return false;
        }

        // Remove the "careOrder." part from the beginning of the coldef's field to match with the invalid fields
        const careOrderField = field.replace('careOrder.', '');
        const invalidFields: string[] = params.data?.warning?.invalidFields ?? [];
        return invalidFields.includes(careOrderField);
      },
    };
  }, []);

  return useMemo(
    () =>
      compact([
        {
          type: 'custom',
          id: 'warning',
          title: '',
          suppressColumnsToolPanel: true,
          suppressHeaderMenuButton: true,
          sortable: false,
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.warning,
          // And explicit padding to center the warning icon somewhat in this unusually narrow column
          cellStyle: params => {
            return { paddingLeft: 5, paddingRight: 0 } satisfies CellStyle;
          },
          params: {
            cellRenderer: (params: ICellRendererParams<SimilarCareOrder>) => {
              const warning = params.data?.warning;
              if (!warning) {
                return '';
              }

              return (
                <Tooltip tooltip="We have identified colliding properties when comparing this Care Order to the Care Order you wish to import. This row cannot be selected for modification. Collisions are highlighted in the row.">
                  <WarningSeverityIcon severity={WarningSeverity.MEDIUM} />
                </Tooltip>
              );
            },
          },
        },
        parser === 'aladdin'
          ? {
              type: 'security',
              field: 'careOrder.Symbol',
              title: 'Symbol',
              width: SIMILAR_CARE_ORDERS_COL_WIDTHS.symbol,
              cellClassRules: cellClassRules,
            }
          : {
              type: 'text',
              field: 'careOrder.Annotations.BasketOrder.BasketSymbol',
              title: 'Fund',
              width: SIMILAR_CARE_ORDERS_COL_WIDTHS.fund,
              cellClassRules: cellClassRules,
            },
        {
          type: 'side',
          field: 'careOrder.Side',
          title: 'Side',
          cellClassRules: cellClassRules,
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.side,
        },
        parser === 'aladdin'
          ? {
              type: 'size',
              field: 'careOrder.OrderQty',
              title: 'Order Qty',
              params: {
                currencyField: 'careOrder.Currency',
              } satisfies SizeColumnParams<SimilarCareOrder>,
              cellClassRules: cellClassRules,
              width: SIMILAR_CARE_ORDERS_COL_WIDTHS.orderQty,
            }
          : {
              type: 'size',
              field: 'careOrder.units',
              title: 'Units',
              cellClassRules: cellClassRules,
              width: SIMILAR_CARE_ORDERS_COL_WIDTHS.units,
            },
        {
          type: 'custom',
          id: 'status',
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.status,
          params: {
            valueGetter: (params: ValueGetterParams<SimilarCareOrder>) => {
              return params.data?.careOrder.status;
            },
            cellRenderer: ({ value }: ICellRendererParams<unknown, ValueOf<typeof CareOrderStatusEnum>>) => {
              if (value) {
                return <CareOrderStatus status={value} />;
              }
              return '';
            },
          },
        },
        {
          type: 'date',
          field: 'careOrder.SubmitTime',
          title: 'Submit Time',
          cellClassRules: cellClassRules,
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.submitTime,
        },
        {
          type: 'text',
          field: 'careOrder.Group',
          title: 'Group',
          cellClassRules: cellClassRules,
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.group,
        },
        {
          type: 'text',
          field: 'careOrder.Annotations.ETP.InitiatingFrom',
          title: 'Initiating From',
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.initiatingFrom,
          cellClassRules: cellClassRules,
        },
        {
          type: 'text',
          field: 'careOrder.Counterparty',
          title: 'Counterparty',
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.counterparty,
          cellClassRules: cellClassRules,
        },
        parser === 'unqork' && {
          type: 'date',
          field: 'careOrder.SettleDate',
          title: 'Settle Date',
          params: {
            customFormatter: formattedUTCDay,
          },
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.settleDate,
          cellClassRules: cellClassRules,
        },
      ] satisfies (ColumnDef<SimilarCareOrder> | false)[]),
    [cellClassRules, parser]
  );
};
